import React, {ChangeEvent, useRef, useState} from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Typography,
} from '@mui/material';
import thumbnail from 'assets/thumbnail.svg';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';

/** Constraint for allowed file types */
export type Mime =
  | 'image/*'
  | 'image/jpeg'
  | 'image/png'
  | 'application/pdf'
  | 'application/msword';

/** Convenience constant callers can import for a 5 MB size cap. */
export const DEFAULT_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export interface UploadPickerProps {
  title: string;
  label: string;
  /**
   * Restrict accepted MIME types. Supports wildcards (e.g. 'image/*').
   * Defaults to ['image/*'].
   */
  allowedMimeTypes?: Mime[];
  /**
   * Maximum allowed file size in bytes. When omitted, no size limit is enforced.
   * Import DEFAULT_MAX_FILE_SIZE_BYTES for the standard 5 MB cap.
   */
  maxFileSizeBytes?: number;
  /**
   * When true, displays a red asterisk (*) next to the title to signal
   * that filling this field is mandatory before submitting the form.
   */
  required?: boolean;
  /**
   * When true (driven by React Hook Form validation after a failed submit),
   * highlights the empty picker card with a red border and shows a helper
   * text to indicate the field is required.
   */
  hasError?: boolean;
  onSelectFiles?: (files: File[]) => void;
  onDeleteFile?: () => void;
}

interface FileEntry {
  file: File;
  preview: string;
  formattedSize: string;
}

const formatFileSize = (size: number): string => {
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${value.toFixed(2)} ${units[unitIndex]}`;
};

const UploadPicker: React.FC<UploadPickerProps> = ({
  title,
  label,
  allowedMimeTypes = ['image/*'] as Mime[],
  maxFileSizeBytes,
  required = false,
  hasError = false,
  onDeleteFile,
  onSelectFiles,
}) => {
  const [file, setFile] = useState<FileEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputFileEl = useRef<HTMLInputElement | null>(null);
  const inputId = useRef(
    `upload-picker-${Math.random().toString(36).slice(2)}`
  );

  const notifyChange = (entry: FileEntry | null) => {
    if (entry) {
      onSelectFiles && onSelectFiles([entry.file]);
    } else {
      onSelectFiles && onSelectFiles([]);
      onDeleteFile && onDeleteFile();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    event.target.value = '';
    if (!selected) return;

    setError(null);

    // Only validate mime type when the parent explicitly restricts it
    if (allowedMimeTypes) {
      // Support wildcards like 'image/*' in addition to exact mime type matches
      const isAllowed = allowedMimeTypes.some(pattern => {
        if (pattern.endsWith('/*')) {
          return selected.type.startsWith(pattern.slice(0, -1));
        }
        return selected.type === pattern;
      });
      if (!isAllowed) {
        const extensions = allowedMimeTypes
          .map(m => {
            const [type, sub] = m.split('/');
            // For wildcards show the category name, otherwise the subtype
            return sub === '*' ? type.toUpperCase() : sub.toUpperCase();
          })
          .join(', ');
        setError(`Solo se permiten archivos en formato: ${extensions}.`);
        return;
      }
    }

    // Only validate size when the parent provides a limit
    if (maxFileSizeBytes !== undefined && selected.size > maxFileSizeBytes) {
      setError(
        `El archivo debe pesar menos de ${formatFileSize(maxFileSizeBytes)}.`
      );
      return;
    }

    const isImage = selected.type.startsWith('image/');
    const entry: FileEntry = {
      file: selected,
      preview: isImage ? URL.createObjectURL(selected) : '',
      formattedSize: formatFileSize(selected.size),
    };
    setFile(entry);
    notifyChange(entry);
  };

  const handleDelete = () => {
    if (file?.preview) URL.revokeObjectURL(file.preview);
    setFile(null);
    notifyChange(null);
  };

  return (
    <Stack className="UploadPicker" spacing={1}>
      <Typography sx={{marginLeft: '8px !important'}} variant="subtitle2">
        {title}
        {/* Red asterisk signals the field is mandatory before the form is submitted */}
        {required && (
          <Typography
            component="span"
            variant="subtitle2"
            color="error"
            aria-hidden="true"
          >
            {' *'}
          </Typography>
        )}
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {file ? (
        <Card sx={{padding: '16px'}}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
              }}
            >
              <CardMedia
                sx={{maxWidth: 48, maxHeight: 48, borderRadius: 1}}
                component="img"
                image={file.preview || thumbnail}
                alt={file.file.name}
              />
              <CardContent>
                <Stack>
                  <Typography variant="subtitle1">{file.file.name}</Typography>
                  <Typography variant="body2">{file.formattedSize}</Typography>
                </Stack>
              </CardContent>
            </Box>
            <CardContent>
              <DeleteIcon
                data-testid="delete-btn"
                sx={{cursor: 'pointer'}}
                onClick={handleDelete}
                color="primary"
              />
            </CardContent>
          </Box>
        </Card>
      ) : (
        <>
          <Card
            component="label"
            htmlFor={inputId.current}
            sx={{
              padding: '16px',
              cursor: 'pointer',
              // Show red border when the field has a validation error
              ...(hasError && {
                border: '1px solid',
                borderColor: 'error.main',
              }),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'row',
                  flexGrow: 1,
                }}
              >
                <CardMedia
                  sx={{maxWidth: 48, maxHeight: 48}}
                  component="img"
                  image={thumbnail}
                  alt="Imagen"
                />
                <CardContent>
                  <Typography color="neutralMedium" variant="subtitle1">
                    {label}
                  </Typography>
                </CardContent>
              </Box>
              <CardContent>
                <CloudUploadIcon sx={{cursor: 'pointer'}} color="primary" />
              </CardContent>
            </Box>
          </Card>
          {/* Helper text shown below the card when validation fails after submit */}
          {hasError && (
            <Typography
              variant="caption"
              color="error"
              sx={{marginLeft: '14px'}}
            >
              Este campo es obligatorio
            </Typography>
          )}
        </>
      )}
      <input
        id={inputId.current}
        data-testid="file-input"
        ref={inputFileEl}
        style={{display: 'none'}}
        type="file"
        // Only set the accept attribute when the parent restricts mime types;
        // omitting it allows the OS file picker to show all file types
        accept={allowedMimeTypes ? allowedMimeTypes.join(',') : undefined}
        onChange={handleFileChange}
      />
    </Stack>
  );
};

export default UploadPicker;
